import React from "react";
import { useNavigate } from "react-router-dom";
import { BaseBox, BaseButton, BaseTypography } from "../exports";
import { ROLE_BASED_ACCESS } from "../../Constant/Security/RoleBasedAccess";
import { TOP_BAR_HEIGHT } from "../layoutConstants";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

export default function BaseSidebar({ items = [], open = false, onToggle }) {
  const footerHeight = 40; // approximate footer height
  const topOffset = TOP_BAR_HEIGHT; // sit flush under the header + breadcrumb bar
  const navigate = useNavigate();

  const getPathForProcess = (id) => {
    switch (id) {
      case "release-management":
        return "/gitflow-release-management";
      case "iac-tfstate-unlock":
        return "/iac-tfstate-unlock";
      case "multi-deployment":
        return "/multi-deployment";
      case "sctk":
        return "/sctk";
      case "aws-secrets-manager":
        return "/aws-secrets-manager";
      case "aws-parameter-store-manager":
        return "/aws-parameter-store-manager";
      case "aws-cache-manager":
        return "/aws-cache-manager";
      case "utils":
        return "/util";
      case "user-management":
        return "/user-management";
      case "role-management":
        return "/role-management";
      case "tenant-management":
        return "/tenant-management";
      case "ai-mode":
        return "/ai-mode";
      default:
        return "/home";
    }
  };

  const processes = [
    ROLE_BASED_ACCESS.RELEASE_MANAGEMENT() && {
      id: "release-management",
      title: "Release Management",
      desc: "Automate GitFlow or Trunk-Based release & hotfix pipelines.",
      icon: "🚀",
    },
    ROLE_BASED_ACCESS.IAC_TFSTATE_UNLOCK() && {
      id: "iac-tfstate-unlock",
      title: "IaC / TF State Unlock",
      desc: "Unlock Terraform remote state locks",
      icon: "🛠️",
    },
    ROLE_BASED_ACCESS.MULTI_DEPLOYMENT() && {
      id: "multi-deployment",
      title: "Multi-Deployment",
      desc: "Deploy to multiple environments with one click.",
      icon: "🧩",
    },
    ROLE_BASED_ACCESS.SCTK() && {
      id: "sctk",
      title: "SCTK",
      desc: "Security and compliance automation toolkit.",
      icon: "🔒",
    },
    ROLE_BASED_ACCESS.AWS_SECRETS_MANAGER() && {
      id: "aws-secrets-manager",
      title: "AWS Secrets Manager",
      desc: "Manage and sync secrets securely across environments.",
      icon: "🗝️",
    },
    ROLE_BASED_ACCESS.AWS_PARAMETER_STORE_MANAGER() && {
      id: "aws-parameter-store-manager",
      title: "AWS Parameter Store Manager",
      desc: "Manage AWS Parameter Store values.",
      icon: "📦",
    },
    ROLE_BASED_ACCESS.AWS_CACHE_MANAGER() && {
      id: "aws-cache-manager",
      title: "AWS Cache Manager",
      desc: "Monitor and manage Redis caches with ease.",
      icon: "⚡",
    },
    ROLE_BASED_ACCESS.UTILS() && {
      id: "utils",
      title: "Utils",
      desc: "Common Utilities",
      icon: "🛠️",
    },
    ROLE_BASED_ACCESS.USER_MANAGEMENT() && {
      id: "user-management",
      title: "User Management",
      desc: "Managing Users across the system",
      icon: "👥",
    },
    ROLE_BASED_ACCESS.ROLE_MANAGEMENT() && {
      id: "role-management",
      title: "Role Management",
      desc: "Control roles across the system",
      icon: "🛡️",
    },
    ROLE_BASED_ACCESS.TENANT_MANAGEMENT() && {
      id: "tenant-management",
      title: "Tenant Management",
      desc: "Manage tenants across the system",
      icon: "🏢",
    },
    ROLE_BASED_ACCESS.AI_MODE() && {
      id: "ai-mode",
      title: "AI Mode",
      desc: "Open AI-powered tools and experiences for delivery workflows.",
      icon: "🤖",
    }
  ].filter(Boolean);

  return (
    <BaseBox
      onMouseEnter={() => onToggle && onToggle(true)}
      onMouseLeave={() => onToggle && onToggle(false)}
      sx={{
        position: "fixed",
        top: `${topOffset}px`,
        left: 0,
        height: `calc(100vh - ${topOffset + footerHeight}px)`, // reduce height to leave space for footer
        width: open ? 220 : 64,
        transition: "width 0.28s, left 0.28s, box-shadow 0.2s",
        backgroundColor: "#ffffffff",
        boxShadow: "0 6px 20px rgba(16,24,40,0.06)",
        borderRight: 0,
        borderRadius: "0 12px 12px 0",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        zIndex: 990,
        overflow: "hidden",
      }}
    >

      {/* Menu Items */}
      <BaseBox sx={{ flex: 1, mt: 1, display: "flex", flexDirection: "column", alignItems: "stretch", px: 1 }}>
        {items.map((item, idx) => (
          <BaseButton
            key={idx}
            onClick={item.onClick}
            sx={{
              display: "flex",
              justifyContent: open ? "flex-start" : "center",
              alignItems: "center",
              width: "100%",
              mb: 1,
              px: 2,
              minHeight: 44,
              textTransform: "none",
              borderRadius: 1,
              color: "text.primary",
              "&:hover": { backgroundColor: "rgba(16,24,40,0.04)" },
            }}
          >
            {item.icon && (
              <span style={{ marginRight: open ? 10 : 0, display: "flex", alignItems: "center" }}>
                {item.icon}
              </span>
            )}
            {open && <BaseTypography sx={{ fontSize: "0.95rem" }}>{item.label}</BaseTypography>}
          </BaseButton>
        ))}
        {processes.length > 0 && (
          <BaseBox sx={{ mt: 1, px: 1 }}>
            {open && <BaseTypography sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 0.5 }}>Menu</BaseTypography>}
            {processes.map((p) => (
              <BaseBox
                key={p.id}
                onClick={() => navigate(getPathForProcess(p.id))}
                sx={{
                  display: "flex",
                  justifyContent: open ? "flex-start" : "center",
                  alignItems: "center",
                  width: "100%",
                  py: 1,
                  px: 2,
                  cursor: "pointer",
                  color: "text.primary",
                  borderRadius: 1,
                  transition: "all 0.2s ease",
                  "&:hover": { 
                    backgroundColor: "rgba(16,24,40,0.06)",
                    color: "primary.main",
                  },
                }}
              >
                <span style={{ marginRight: open ? 10 : 0, display: "flex", alignItems: "center" }}>{p.icon}</span>
                {open && <BaseTypography sx={{ fontSize: "0.9rem" }}>{p.title}</BaseTypography>}
              </BaseBox>
            ))}
          </BaseBox>
        )}
      </BaseBox>
    </BaseBox>
  );
}
